import React from 'react'
import {
	Box,
	Burger,
	Button,
	Center,
	Divider,
	Drawer,
	Group,
	HoverCard,
	List,
	rem,
	ScrollArea,
	SimpleGrid,
	Text,
} from '@mantine/core'
import Link from 'next/link'
import { useDisclosure } from '@mantine/hooks'
import { IconChevronDown } from '@tabler/icons-react'
import { useAppSelector } from './../state/app-store'
import { useRouter } from 'next/router'
import classNames from 'classnames'
import * as AppStore from './../state/app-store'

// import { useIsMobile } from '../lib/useIsMobile'
import { VersionContext } from '../contexts/VersionContext'
import * as Selectors from './../state/selectors'
import * as Actions from './../state/actions'
import AccountModal from './AccountModal'
import CountryBadge from './CountryBadge'

import { sluggify } from '../lib/helper'
import * as Consts from 'consts'
import * as Types from 'declarations'

type ContinentChild = {
	continentName: string
	blurb: string
	climbBlurb: string
	countries: { label: string; href: string; isoCode: string }[]
}

type SimpleChild = {
	label: string
	href: string
}

type MenuItem = {
	label: string
	href: string
	isActive: boolean
	children?: ContinentChild[][] | SimpleChild[]
}

const isSimpleChildArray = (children: any): children is SimpleChild[] => {
	return (
		Array.isArray(children) &&
		children.length > 0 &&
		'label' in children[0] &&
		'href' in children[0]
	)
}

const isContinentChildArrayArray = (
	children: any,
): children is ContinentChild[][] => {
	return (
		Array.isArray(children) &&
		Array.isArray(children[0]) &&
		children[0].length > 0 &&
		'continentName' in children[0][0]
	)
}

const BrandTitle = () => {
	// const isMobile = useIsMobile()
	const version = React.useContext(VersionContext)?.version

	return (
		<Link href="/" legacyBehavior>
			<a className="brand-title">
				{/* plain img: Mantine 9 Image no longer applies width/height/object-fit
				    to the img itself, which distorted the logo (v6 inline-style parity) */}
				<img
					src={
						version === Types.VERSION.PEAKS
							? '/branding/guided-peaks.png'
							: '/branding/guided-trekking.png'
					}
					alt={
						version === Types.VERSION.PEAKS ? 'Guided Peaks' : 'Guided Trekking'
					}
					title={
						version === Types.VERSION.PEAKS ? 'Guided Peaks' : 'Guided Trekking'
					}
					height={40}
					width={version === Types.VERSION.PEAKS ? 121 : 133}
					style={{
						objectFit: 'cover',
						width: version === Types.VERSION.PEAKS ? 121 : 133,
						height: 40,
					}}
				/>
			</a>
		</Link>
	)
}

const HeaderMegaMenu = () => {
	const router = useRouter()
	const [
		drawerOpened,
		{ toggle: toggleDrawer, close: closeDrawer },
	] = useDisclosure(false)

	const dispatch = AppStore.useAppDispatch()

	React.useEffect(() => {
		// Check if the URL has the specific hash
		if (router.asPath.includes('#open-login-guide')) {
			dispatch(Actions.openLogin())
		}
	}, [router.asPath, dispatch])

	const authedUser = useAppSelector(Selectors.authedUser)
	const isAdmin = useAppSelector(Selectors.isUserAdmin)
	const version = React.useContext(VersionContext).version
	const isPeaks = version === 'PEAKS'

	const menuItemsRaw: MenuItem[] = [
		{
			label: isPeaks ? 'Expeditions' : 'Treks',
			href: '/expedition-explorer',
			isActive: [
				'/expeditions',
				'/expedition-explorer',
				`/[routeType]/[continent]/[countries]/[slug]`,
				`/[routeType]/[continent]/[countries]/[slug]/[...guidesSlug]`,
			].some((route) => {
				return router.pathname === route
			}),
			children: [
				{ label: 'Explorer View', href: '/expedition-explorer' },
				{ label: 'List View', href: '/expeditions' },
			],
		},
		...(isPeaks
			? [
					{
						label: 'Guides',
						href: '/guides',
						isActive: router.pathname.startsWith('/guides'),
					},
					{
						label: 'Articles',
						href: '/articles',
						isActive: router.pathname.startsWith('/articles'),
					},
					{
						label: 'Countries',
						href: '/countries',
						isActive:
							router.pathname.startsWith('/countries') ||
							router.pathname === `/[routeType]/[continent]/[countries]` ||
							router.pathname === `/[routeType]/[continent]`,
						children: Consts.regions.map((region) =>
							region.map((continent) => ({
								continentName: continent.continent,
								blurb: continent.blurb,
								climbBlurb: continent.climbBlurb,
								countries: continent.countries,
							})),
						),
					},
					{
						label: 'About',
						href: '/about',
						isActive: router.pathname === '/about',
					},
			  ]
			: []),
	]

	const closeMenuOnClick = () => {
		closeDrawer()
	}
	const countryMenuMobile = Consts.regions.map((region, idxRegion) => (
		<Box ml="xl" mt="lg" key={idxRegion}>
			{region.map((continent, idxContinent) => (
				<div key={idxContinent}>
					<Link
						href={`/climbing/${sluggify(continent.continent)}`}
						onClick={closeMenuOnClick}
					>
						<Text
							size="sm"
							fw={600}
							// m="sm"
							mb="sm"
							className={classNames({
								'header-link': true,
								'active-link': [
									'/countries',
									`/[routeType]/[continent]`,
									`/[routeType]/[continent]/[countries]`,
								].some((route) => {
									return router.pathname === route
								}),
							})}
						>
							{continent.continent}
						</Text>
					</Link>
					<div style={{ display: 'block' }}>
						{continent.countries.map((country, idxCountry) => (
							<Link
								href={country.href}
								className="header-link"
								key={idxCountry}
								style={{
									margin: '0 8px 8px 0',
									display: 'inline-block',
									width: 'auto',
								}}
								onClick={closeMenuOnClick}
							>
								<CountryBadge isoCode={country.isoCode} />
							</Link>
						))}
					</div>
				</div>
			))}
		</Box>
	))

	const headerTextSize = 'md'

	return (
		<div id="header2">
			<header>
				<Group
					style={{
						justifyContent: 'space-between',
						// zIndex: 10005, // remove as conflicted with modal
						position: 'relative',
						backgroundColor: '#fff',
					}}
					h="100%"
					id="brand"
				>
					<BrandTitle />

					<div className="mobile-hide">
						<Group>
							{/* <> */}
							{menuItemsRaw.map((menuItem, idxMenuItem) => {
								return !menuItem?.children ? (
									<Link href={menuItem.href} legacyBehavior key={idxMenuItem}>
										<a className={'header-link'}>
											<Text
												size={headerTextSize}
												m="sm"
												fw={500}
												className={classNames({
													'header-link': true,
													'active-link': menuItem.isActive,
												})}
											>
												{menuItem.label}
											</Text>
										</a>
									</Link>
								) : isContinentChildArrayArray(menuItem.children) ? (
									<HoverCard
										position="bottom"
										radius="md"
										shadow="md"
										withinPortal
										key={idxMenuItem}
									>
										<HoverCard.Target>
											<Link href={menuItem.href} className={'header-link'}>
												<Center inline>
													<Box component="span" mr={5}>
														<Text
															size={headerTextSize}
															m="sm"
															fw={500}
															className={classNames({
																'header-link': true,
																'active-link': menuItem.isActive,
															})}
														>
															{menuItem.label}
														</Text>
													</Box>
													<IconChevronDown
														style={{
															width: rem(16),
															height: rem(16),
														}}
													/>
												</Center>
											</Link>
										</HoverCard.Target>

										<HoverCard.Dropdown
											style={{ overflow: 'hidden', width: '600px' }}
										>
											<SimpleGrid cols={3} spacing={0}>
												{menuItem.children.map((region, idxRegion) => (
													<div key={idxRegion}>
														{region.map((continent, idxContinent) => (
															<div key={idxContinent}>
																<Link
																	href={`/climbing/${sluggify(
																		continent.continentName,
																	)}`}
																>
																	<Text
																		size="sm"
																		fw={600}
																		m="sm"
																		className={classNames({
																			'header-link': true,
																			'active-link': [
																				'/countries',
																				`/[routeType]/[continent]`,
																				`/[routeType]/[continent]/[countries]`,
																			].some((route) => {
																				return router.pathname === route
																			}),
																		})}
																	>
																		{continent.continentName}
																	</Text>
																</Link>
																<List listStyleType="none">
																	{continent.countries.map((country) => (
																		<List.Item key={country.label}>
																			<Link
																				href={country.href}
																				className="header-link"
																			>
																				<Text size="sm" m="xs" fw={500}>
																					<CountryBadge
																						isoCode={country.isoCode}
																					/>
																				</Text>
																			</Link>
																		</List.Item>
																	))}
																</List>
															</div>
														))}
													</div>
												))}
											</SimpleGrid>
										</HoverCard.Dropdown>
									</HoverCard>
								) : isSimpleChildArray(menuItem.children) ? (
									<HoverCard
										position="bottom"
										radius="md"
										shadow="md"
										withinPortal
										key={idxMenuItem}
									>
										<HoverCard.Target>
											<Link href={menuItem.href} className={'header-link'}>
												<Center inline>
													<Box component="span" mr={5}>
														<Text
															size={headerTextSize}
															m="sm"
															fw={500}
															className={classNames({
																'header-link': true,
																'active-link': menuItem.isActive,
															})}
														>
															{menuItem.label}
														</Text>
													</Box>
													<IconChevronDown
														style={{
															width: rem(16),
															height: rem(16),
														}}
													/>
												</Center>
											</Link>
										</HoverCard.Target>

										<HoverCard.Dropdown style={{ overflow: 'hidden' }}>
											<div>
												{menuItem.children.map((item, idx) => (
													<Link
														key={idx}
														href={item.href}
														className="header-link"
													>
														<Text size="sm" m="xs" fw={500}>
															{item.label}
														</Text>
													</Link>
												))}
											</div>
										</HoverCard.Dropdown>
									</HoverCard>
								) : null
							})}
							{/* </> */}
						</Group>
					</div>

					<div className="mobile-hide">
						<Group p="xs">
							{isAdmin && (
								<Link href="/admin" passHref>
									<Button
										title={`logged in as ${authedUser?.id} (${authedUser?.role})`}
									>
										Admin
									</Button>
								</Link>
							)}
							<AccountModal />
						</Group>
					</div>

					<Burger
						opened={drawerOpened}
						onClick={toggleDrawer}
						className="mobile-show"
						mr="sm"
						title="Open navigation"
						aria-label="Open navigation"
					/>
				</Group>
			</header>

			<Drawer
				opened={drawerOpened}
				onClose={closeDrawer}
				size="100%"
				padding={0}
				title={null}
				className="mobile-show"
				zIndex={10000}
				position="top"
				closeButtonProps={{
					size: 'xl',
					iconSize: 32,
					style: {
						position: 'fixed',
						top: '16px',
						right: '16px',
						zIndex: 10001,
						backgroundColor: '#fff',
						borderRadius: '50%',
						boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
					},
				}}
			>
				<Box
					style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}
				>
					<ScrollArea
						style={{ flex: 1 }}
						styles={{
							root: {
								backgroundColor: '#fff',
							},
							viewport: {
								padding: '16px',
							},
						}}
					>
						<div
							style={{ maxWidth: '100%', margin: '0 auto', paddingTop: '40px' }}
						>
							{menuItemsRaw.map((menuItem, idxMenuItem) => {
								return !menuItem?.children ? (
									<Box key={idxMenuItem} mb="md">
										<Link href={menuItem.href} legacyBehavior>
											<a className={'header-link'}>
												<Box
													p="md"
													style={{
														borderRadius: '8px',
														backgroundColor: '#f8f9fa',
													}}
												>
													<Text size="lg" fw={500} onClick={closeMenuOnClick}>
														{menuItem.label}
													</Text>
												</Box>
											</a>
										</Link>
									</Box>
								) : isContinentChildArrayArray(menuItem.children) ? (
									<Box key={idxMenuItem} mb="xl">
										<Text size="xl" fw={600} mb="md" color="dimmed">
											{menuItem.label}
										</Text>
										{menuItem.children.map((region, idxRegion) => (
											<div key={idxRegion}>
												{region.map((continent, idxContinent) => (
													<Box key={idxContinent} mb="xl">
														<Link
															href={`/climbing/${sluggify(
																continent.continentName,
															)}`}
															onClick={closeMenuOnClick}
														>
															<Text
																size="lg"
																fw={600}
																mb="md"
																color="red"
																style={{ textTransform: 'uppercase' }}
															>
																{continent.continentName}
															</Text>
														</Link>
														<SimpleGrid
															cols={{ base: 1, xs: 1, md: 2, lg: 3 }}
															spacing="xs"
														>
															{continent.countries.map((country) => (
																<Link
																	key={country.label}
																	href={country.href}
																	className="header-link"
																	onClick={closeMenuOnClick}
																>
																	<Box
																		p="sm"
																		style={{
																			borderRadius: '8px',
																			backgroundColor: '#f8f9fa',
																			display: 'inline-block',
																			width: '100%',
																		}}
																	>
																		<CountryBadge isoCode={country.isoCode} />
																	</Box>
																</Link>
															))}
														</SimpleGrid>
													</Box>
												))}
											</div>
										))}
									</Box>
								) : isSimpleChildArray(menuItem.children) ? (
									<Box key={idxMenuItem} mb="xl">
										<Text size="xl" fw={600} mb="md" color="dimmed">
											{menuItem.label}
										</Text>
										<Box pl="md" style={{ borderLeft: '4px solid #f1f3f5' }}>
											{menuItem.children.map((item, idx) => (
												<Box mb="xs" key={idx}>
													<Link
														href={item.href}
														className="header-link"
														onClick={closeMenuOnClick}
													>
														<Box
															p="md"
															style={{
																borderRadius: '8px',
																backgroundColor: '#f8f9fa',
															}}
														>
															<Text size="md" fw={500}>
																{item.label}
															</Text>
														</Box>
													</Link>
												</Box>
											))}
										</Box>
									</Box>
								) : null
							})}
						</div>
					</ScrollArea>
					<Box
						p="md"
						style={{
							borderTop: '1px solid #e9ecef',
							backgroundColor: '#fff',
						}}
					>
						<Group grow>
							{isAdmin && (
								<Link href="/admin" passHref>
									<Button
										fullWidth
										title={`logged in as ${authedUser?.id} (${authedUser?.role})`}
										className="header-mobile-button-group-button"
									>
										Admin
									</Button>
								</Link>
							)}
							<AccountModal />
						</Group>
					</Box>
				</Box>
			</Drawer>
		</div>
	)
}

export default HeaderMegaMenu
